/**
 * Single-flight request de-duplication with cooperative, reference-counted
 * abort.
 *
 * While a run for a given `key` is in flight, additional callers share that one
 * run instead of starting their own — so the same attachment is never
 * downloaded and decrypted more than once concurrently (e.g. the open viewer,
 * its ±1 prefetch, and auto-download all wanting the same image at once).
 *
 * Abort is cooperative: every caller passes its own AbortSignal, and the shared
 * run is aborted ONLY once every current caller has aborted. So one caller
 * navigating away can't cancel work the others still need, but once nobody is
 * waiting the run is cancelled instead of finishing wasted work.
 *
 * The map entry is removed as soon as the run settles, so a later request for
 * the same key re-runs (and picks up whatever the finished run cached).
 *
 * `run` is invoked synchronously so concurrent callers in the same tick share
 * the entry; a synchronous throw from it is converted to a rejection of the
 * shared promise, so the entry is still created and cleaned up rather than
 * bypassed.
 */
type InFlightEntry = {
  promise: Promise<unknown>;
  controller: AbortController;
  waiters: number;
};

const inFlight = new Map<string, InFlightEntry>();

export function runDeduplicated<T>(
  key: string,
  callerSignal: AbortSignal | undefined,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  let entry = inFlight.get(key);

  // Never JOIN a run that is already aborting. The deferred-abort microtask
  // below only rescues a same-tick handoff; a caller arriving in a LATER task
  // (close→reopen, a bubble unmounting and the viewer opening on the next tick)
  // would otherwise attach to a doomed run and receive its null — surfacing as
  // a spurious "This attachment couldn't be loaded". The dying entry stays in
  // the map until it settles, where its own `finally` sees it is no longer the
  // current entry and leaves this fresh one alone.
  if (entry?.controller.signal.aborted) {
    inFlight.delete(key);
    entry = undefined;
  }

  if (!entry) {
    const controller = new AbortController();
    const created: InFlightEntry = {
      controller,
      waiters: 0,
      promise: Promise.resolve(),
    };
    // Call run synchronously (so same-tick callers share this entry), but turn a
    // synchronous throw into a rejection — otherwise it would escape
    // runDeduplicated and bypass the map entry and its cleanup below.
    const started = (() => {
      try {
        return run(controller.signal);
      } catch (error) {
        return Promise.reject(error);
      }
    })();
    created.promise = started.finally(() => {
      // Only clear if we're still the current entry (defensive; a settled run
      // is deleted before any later run could replace it).
      if (inFlight.get(key) === created) {
        inFlight.delete(key);
      }
    });
    inFlight.set(key, created);
    entry = created;
  }

  const current = entry;
  current.waiters += 1;

  // Each caller decrements exactly once — whichever comes first, its own abort
  // or the shared run settling. When the count reaches zero the shared run is
  // aborted so it stops instead of finishing wasted — but the abort is DEFERRED
  // to a microtask that re-checks the count. This closes a handoff race: when
  // one caller leaves and another joins in the SAME tick (e.g. swiping to an
  // item whose ±1 prefetch is torn down in the same React commit that mounts
  // the viewer on it), the joiner bumps the count back above zero before the
  // microtask fires, so the shared fetch survives instead of being cancelled
  // out from under it (which surfaced as a spurious "couldn't be loaded").
  let released = false;
  const release = () => {
    if (released) {
      return;
    }
    released = true;
    current.waiters -= 1;
    if (current.waiters <= 0) {
      queueMicrotask(() => {
        if (current.waiters <= 0) {
          current.controller.abort();
        }
      });
    }
  };

  if (callerSignal?.aborted) {
    release();
  } else if (callerSignal) {
    callerSignal.addEventListener("abort", release, { once: true });
  }

  return (current.promise as Promise<T>).finally(() => {
    callerSignal?.removeEventListener("abort", release);
    release();
  });
}

/** Test-only: clear all in-flight state between cases. */
export function __resetDedupeInFlight() {
  inFlight.clear();
}
